/**
 * Significant figures: counting them, rounding to them, and judging a typed
 * answer against what a calculation should carry.
 *
 * The rules are the standard ones: leading zeros never count, zeros between
 * digits always do, trailing zeros count only when a decimal point is present
 * or the number is written in scientific notation, and a bare integer like 100
 * is genuinely ambiguous — treated as one significant figure, and said out loud
 * rather than quietly assumed.
 */

export interface SigFigCount {
  /** How many significant figures the written form carries. */
  count: number;
  /** True for a bare integer with trailing zeros, e.g. 100. */
  ambiguous: boolean;
  /** Digits after the decimal point, for addition and subtraction. */
  decimals: number;
  value: number;
  /** The number without its units. */
  numberText: string;
  /** Whatever followed the number. */
  units: string;
}

export class SigFigError extends Error {}

/** Accepts 5, 5.0, 0.5, .5 and 100. — a trailing point is how "100." says three figures. */
const NUMBER = String.raw`[+-]?(?:\d+\.?\d*|\.\d+)`;
const SCIENTIFIC = new RegExp(
  `^(${NUMBER})\\s*(?:(?:[eE])|(?:[x×*]\\s*10\\s*\\^?))\\s*([+-]?\\d+)\\s*(.*)$`,
);
const PLAIN = new RegExp(`^(${NUMBER})\\s*(.*)$`);

/**
 * Splits "1.20e3 g" or "1.20 × 10^3 g" or "0.00450 mol" into its parts.
 * Returns null when there is no number at the front at all.
 */
export function parseQuantity(input: string): {
  mantissa: string;
  exponent: number | null;
  units: string;
} | null {
  const text = (input ?? '').trim();
  if (!text) return null;

  const sci = SCIENTIFIC.exec(text);
  if (sci) {
    return { mantissa: sci[1]!, exponent: Number(sci[2]), units: (sci[3] ?? '').trim() };
  }

  const plain = PLAIN.exec(text);
  if (plain) {
    return { mantissa: plain[1]!, exponent: null, units: (plain[2] ?? '').trim() };
  }
  return null;
}

/** Counts the significant figures in a written number. */
export function countSigFigs(input: string): SigFigCount {
  const parsed = parseQuantity(input);
  if (!parsed) throw new SigFigError('That is not a number.');

  const { mantissa, exponent, units } = parsed;
  const negative = mantissa.startsWith('-');
  const digitsOnly = mantissa.replace(/^[+-]/, '');

  if (!/\d/.test(digitsOnly)) throw new SigFigError('That is not a number.');

  const hasPoint = digitsOnly.includes('.');
  const [whole = '', fraction = ''] = digitsOnly.split('.');

  // Strip leading zeros; they are never significant.
  const allDigits = (whole + fraction).replace(/^0+/, '');

  let count: number;
  let ambiguous = false;

  if (allDigits === '') {
    // The number is zero in some written form: 0, 0.00, 0.0
    count = hasPoint ? fraction.length : 1;
  } else if (hasPoint) {
    // With a decimal point every trailing zero counts.
    count = allDigits.length;
  } else if (exponent !== null) {
    // Scientific notation makes the intent explicit.
    count = allDigits.length;
  } else {
    // A bare integer: trailing zeros cannot be told apart from placeholders.
    const trimmed = allDigits.replace(/0+$/, '');
    if (trimmed.length !== allDigits.length) {
      ambiguous = true;
      count = Math.max(trimmed.length, 1);
    } else {
      count = allDigits.length;
    }
  }

  const base = Number(digitsOnly);
  const value = (negative ? -base : base) * (exponent === null ? 1 : 10 ** exponent);

  return {
    count,
    ambiguous,
    decimals: hasPoint ? fraction.length : 0,
    value,
    numberText: mantissa + (exponent === null ? '' : `e${exponent}`),
    units,
  };
}

/** Rounds to a number of significant figures, keeping trailing zeros. */
export function roundToSigFigs(value: number, figures: number): string {
  if (!Number.isFinite(value)) throw new SigFigError('That is not a finite number.');
  if (figures < 1 || figures > 15 || !Number.isInteger(figures)) {
    throw new SigFigError('Significant figures must be a whole number from 1 to 15.');
  }
  if (value === 0) return figures === 1 ? '0' : `0.${'0'.repeat(figures - 1)}`;

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const places = figures - 1 - exponent;

  // Very large or very small numbers read better in scientific notation, and
  // plain decimal cannot express their significant figures honestly anyway.
  if (exponent >= figures + 3 || exponent < -4) {
    const mantissa = value / 10 ** exponent;
    return `${mantissa.toFixed(figures - 1)}e${exponent}`;
  }

  const plain =
    places <= 0
      ? String(Math.round(value / 10 ** -places) * 10 ** -places)
      : value.toFixed(places);

  // A plain integer cannot always say how many figures it carries: "16000"
  // rounded to 3 reads as 1. Where the written form would lie, say it in
  // scientific notation instead.
  if (countSigFigs(plain).count !== figures) {
    const mantissa = value / 10 ** exponent;
    return `${mantissa.toFixed(figures - 1)}e${exponent}`;
  }
  return plain;
}

// ------------------------------------------------------------ expectations

export type SigfigRule = 'min-of-inputs' | 'decimal-places' | 'log-mantissa' | number;

export interface RuleInput {
  /** The value as it was written, so its zeros can be read. */
  text: string;
  /**
   * Exact numbers never limit a result: defined conversions, counted objects,
   * and — as specified — atomic masses taken from the table.
   */
  exact?: boolean;
}

export type Expectation =
  | { kind: 'sigfigs'; n: number }
  | { kind: 'decimals'; n: number }
  | { kind: 'none'; why: string };

export function expectationFor(rule: SigfigRule, inputs: RuleInput[]): Expectation {
  if (typeof rule === 'number') {
    if (!Number.isInteger(rule) || rule < 1 || rule > 15) {
      return { kind: 'none', why: 'The template asks for an impossible number of figures.' };
    }
    return { kind: 'sigfigs', n: rule };
  }

  const limiting = inputs.filter((i) => !i.exact);
  if (limiting.length === 0) {
    return { kind: 'none', why: 'Every input is an exact number, so nothing limits the answer.' };
  }

  const counted = limiting.map((i) => countSigFigs(i.text));

  if (rule === 'min-of-inputs') {
    return { kind: 'sigfigs', n: Math.min(...counted.map((c) => c.count)) };
  }
  if (rule === 'decimal-places') {
    return { kind: 'decimals', n: Math.min(...counted.map((c) => c.decimals)) };
  }
  // log-mantissa: the digits after the point in a log match the input's sig figs.
  return { kind: 'decimals', n: Math.min(...counted.map((c) => c.count)) };
}

// ----------------------------------------------------------------- grading

export type SigfigMode = 'strict' | 'lenient';

export interface Tolerance {
  relative?: number;
  absolute?: number;
}

export interface CheckInput {
  typed: string;
  expectedValue: number;
  answerUnits: string;
  unitAliases: string[];
  rule: SigfigRule;
  ruleInputs: RuleInput[];
  tolerance?: Tolerance;
  mode: SigfigMode;
}

export interface CheckResult {
  correct: boolean;
  valueOk: boolean;
  unitsOk: boolean;
  sigFigsOk: boolean;
  ambiguous: boolean;
  expected: Expectation;
  got: SigFigCount | null;
  /** What to show the student: one line, plain words. */
  message: string;
}

const DEFAULT_TOLERANCE: Tolerance = { relative: 0.01 };

function unitsMatch(typed: string, answerUnits: string, aliases: string[]): boolean {
  // An empty answerUnits means the answer is unitless — pH and the like — and
  // nothing need be typed.
  if (!answerUnits.trim()) return true;
  const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, '');
  const got = normalize(typed);
  if (!got) return false;
  const accepted = new Set([answerUnits, ...aliases].map(normalize));
  return accepted.has(got);
}

export function checkAnswer(input: CheckInput): CheckResult {
  const {
    typed,
    expectedValue,
    answerUnits,
    unitAliases,
    rule,
    ruleInputs,
    tolerance = DEFAULT_TOLERANCE,
    mode,
  } = input;

  let got: SigFigCount;
  try {
    got = countSigFigs(typed);
  } catch {
    return {
      correct: false,
      valueOk: false,
      unitsOk: false,
      sigFigsOk: false,
      ambiguous: false,
      expected: expectationFor(rule, ruleInputs),
      got: null,
      message: 'That does not read as a number. Try something like 63.5 or 1.20e3.',
    };
  }

  const relative = tolerance.relative ?? DEFAULT_TOLERANCE.relative!;
  const absolute = tolerance.absolute;
  const gap = Math.abs(got.value - expectedValue);
  const valueOk =
    absolute !== undefined
      ? gap <= absolute
      : gap <= Math.abs(expectedValue) * relative || gap < 1e-12;

  const unitsOk = unitsMatch(got.units, answerUnits, unitAliases);
  const expected = expectationFor(rule, ruleInputs);

  let sigFigsOk = true;
  if (expected.kind === 'sigfigs') sigFigsOk = got.count === expected.n;
  else if (expected.kind === 'decimals') sigFigsOk = got.decimals === expected.n;

  // Units are wrong in both modes; significant figures only bite in strict mode.
  const correct = valueOk && unitsOk && (mode === 'strict' ? sigFigsOk : true);

  return {
    correct,
    valueOk,
    unitsOk,
    sigFigsOk,
    ambiguous: got.ambiguous,
    expected,
    got,
    message: messageFor({ valueOk, unitsOk, sigFigsOk, got, expected, answerUnits, mode, correct }),
  };
}

function messageFor(args: {
  valueOk: boolean;
  unitsOk: boolean;
  sigFigsOk: boolean;
  got: SigFigCount;
  expected: Expectation;
  answerUnits: string;
  mode: SigfigMode;
  correct: boolean;
}): string {
  const { valueOk, unitsOk, sigFigsOk, got, expected, answerUnits, mode, correct } = args;

  if (!valueOk) return 'The number is not right.';
  if (!unitsOk) {
    return got.units
      ? `The number is right but the units are not — this answer is in ${answerUnits}.`
      : `The number is right, but it needs units: ${answerUnits}.`;
  }

  if (!sigFigsOk && expected.kind !== 'none') {
    const want =
      expected.kind === 'sigfigs'
        ? `${expected.n} significant figure${expected.n === 1 ? '' : 's'}`
        : `${expected.n} decimal place${expected.n === 1 ? '' : 's'}`;
    const have =
      expected.kind === 'sigfigs'
        ? `${got.count} significant figure${got.count === 1 ? '' : 's'}`
        : `${got.decimals} decimal place${got.decimals === 1 ? '' : 's'}`;
    const ambiguity = got.ambiguous
      ? ' A whole number with trailing zeros is ambiguous, so it counts as one significant figure — write it with a decimal point or in scientific notation to be clear.'
      : '';
    return mode === 'strict'
      ? `Right value, but it should have ${want} and you wrote ${have}.${ambiguity}`
      : `Correct. Worth noting: this should have ${want} and you wrote ${have}.${ambiguity}`;
  }

  if (got.ambiguous && !correct) {
    return 'A whole number with trailing zeros is ambiguous. Write it with a decimal point or in scientific notation.';
  }

  return 'Correct.';
}
